import React, { useContext, useEffect, useRef, useState } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";
import toast from "react-hot-toast";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import { IoMdArrowBack } from "react-icons/io";
import { FiUploadCloud, FiX, FiImage, FiCheck } from "react-icons/fi";
import { FaGift, FaWandMagicSparkles, FaXmark } from "react-icons/fa6";
import {
  getCategoryListingPaginate,
  getManufacturesListingPaginate,
  getSubCategoryListingPaginate,
} from "../../api/context/manufacturer";
import { getBrandListing } from "../../api/context/brand";
import { createAiProduct } from "../../api/context/products";
import { SelectionContext } from "../../Context/Store";
import PaywallModal from "../Billing/components/PaywallModal";
import InvalidImageModal from "./InvalidImageModal";

import goodLit from "../../assets/images/guidelines/good-lit.webp";
import goodBg from "../../assets/images/guidelines/good-bg.webp";
import goodFrame from "../../assets/images/guidelines/good-frame.webp";
import goodAngle from "../../assets/images/guidelines/good-angle.webp";
import badLit from "../../assets/images/guidelines/bad-lit.webp";
import badBg from "../../assets/images/guidelines/bad-bg.webp";
import badFrame from "../../assets/images/guidelines/bad-frame.webp";
import badBlur from "../../assets/images/guidelines/bad-blur.webp";

const GUIDELINE_IMAGES = {
  "good-lit": goodLit,
  "good-bg": goodBg,
  "good-frame": goodFrame,
  "good-angle": goodAngle,
  "bad-lit": badLit,
  "bad-bg": badBg,
  "bad-frame": badFrame,
  "bad-blur": badBlur,
};

const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const ACCEPTED_MIME = ["image/jpeg", "image/jpg", "image/png"];

const LOADING_STEPS = [
  { text: "Uploading photos...", duration: 6000 },
  { text: "Analyzing the image...", duration: 7000 },
  { text: "Extracting product details...", duration: 7000 },
  { text: "Detecting product color and style...", duration: 7000 },
  { text: "Writing product title...", duration: 4000 },
  { text: "Writing product description...", duration: 6000 },
  { text: "Polishing the final draft...", duration: 7000 },
  { text: "Almost done...", duration: null },
];

const Dropzone = ({ label, preview, error, onFile, onRemove }) => {
  const inputRef = useRef();
  const [dragging, setDragging] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) onFile(file);
  };

  return (
    <div className="ai-dropzone-wrap">
      <div className="ai-dropzone-label">
        <FiImage /> {label}
      </div>
      <div
        className={[
          "ai-dropzone",
          dragging ? "dragging" : "",
          preview ? "has-image" : "",
          error ? "has-error" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={() => !preview && inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
      >
        {preview ? (
          <>
            <img src={preview} alt={label} className="ai-dropzone-preview" />
            <button
              type="button"
              className="ai-dropzone-remove"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              aria-label={`Remove ${label}`}
            >
              <FiX />
            </button>
          </>
        ) : (
          <div className="ai-dropzone-empty">
            <FiUploadCloud className="ai-upload-icon" />
            <div className="ai-dropzone-primary">
              Drop image or <span className="link">browse</span>
            </div>
            <div className="ai-dropzone-secondary">JPG · PNG · up to 10 MB</div>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onFile(file);
            e.target.value = "";
          }}
        />
      </div>
      {error && <div className="ai-error">{error}</div>}
    </div>
  );
};

const GuidelineItem = ({ variant, label }) => (
  <div className="ai-guideline-item">
    <div className="ai-guideline-thumb">
      <img src={GUIDELINE_IMAGES[variant]} alt={label} />
    </div>
    <div className="ai-guideline-label">{label}</div>
  </div>
);

const CreateAiInventory = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user")) || {};
  const isVendor = user?.userType === "Vendor";

  const vendorSelfOption = isVendor
    ? {
        value: user?.vendorId,
        label:
          [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
          user?.storeName ||
          user?.email ||
          "Your Store",
      }
    : null;

  const [frontFile, setFrontFile] = useState(null);
  const [backFile, setBackFile] = useState(null);
  const [frontPreview, setFrontPreview] = useState("");
  const [backPreview, setBackPreview] = useState("");
  const [imageErrors, setImageErrors] = useState({ front: "", back: "" });

  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const [manufacturerOptions, setManufacturerOptions] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);

  const [submitting, setSubmitting] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);

  const { billingBalance, refreshBillingBalance, hasAttemptedAi, markAttemptedAi } = useContext(SelectionContext);

  useEffect(() => {
    if (isVendor) refreshBillingBalance();
  }, [isVendor, refreshBillingBalance]);

  const totalAvailable = billingBalance?.totalAvailable ?? null;
  const trialAvailableOnBalance = billingBalance?.trial?.available === true;
  const trialGrantAmount = billingBalance?.trial?.grantAmount ?? 3;
  const hasClaimableTrial = isVendor && trialAvailableOnBalance;
  const hasBalance = totalAvailable != null && totalAvailable > 0;
  const isTrialOnly = hasClaimableTrial && !hasBalance;
  const isOutOfCredits =
    isVendor && hasAttemptedAi && totalAvailable === 0 && !hasClaimableTrial;
  const [paywall, setPaywall] = useState(null);
  const [invalidImage, setInvalidImage] = useState(null);

  useEffect(() => {
    return () => {
      if (frontPreview) URL.revokeObjectURL(frontPreview);
      if (backPreview) URL.revokeObjectURL(backPreview);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!submitting) {
      setLoadingStep(0);
      return;
    }
    const timeouts = [];
    let cumulative = 0;
    for (let i = 1; i < LOADING_STEPS.length; i++) {
      cumulative += LOADING_STEPS[i - 1].duration ?? 0;
      timeouts.push(setTimeout(() => setLoadingStep(i), cumulative));
    }
    return () => timeouts.forEach(clearTimeout);
  }, [submitting]);

  const schema = yup.object().shape({
    categoryId: yup
      .number()
      .typeError("Category is required")
      .required("Category is required"),
    subCategoryId: yup
      .number()
      .typeError("Sub-category is required")
      .required("Sub-category is required"),
    manufacturerId: isVendor
      ? yup.mixed()
      : yup
          .number()
          .typeError("Vendor is required")
          .required("Vendor is required"),
  });

  const formik = useFormik({
    initialValues: {
      categoryId: "",
      subCategoryId: "",
      manufacturerId: isVendor ? user?.vendorId ?? "" : "",
      brandId: "",
      selectedCategory: null,
      selectedSubCategory: null,
      selectedManufacturer: vendorSelfOption,
      selectedBrand: null,
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      if (!frontFile) {
        setImageErrors((p) => ({ ...p, front: "Front image is required" }));
        return;
      }
      setSubmitting(true);
      try {
        const fd = new FormData();
        fd.append("frontImage", frontFile);
        if (backFile) fd.append("backImage", backFile);
        fd.append("categoryId", values.categoryId);
        fd.append("subCategoryId", values.subCategoryId);
        fd.append("subSubCategoryId", values.subCategoryId);
        if (values.selectedCategory?.label)
          fd.append("categoryName", values.selectedCategory.label);
        if (values.selectedSubCategory?.label)
          fd.append("subCategoryName", values.selectedSubCategory.label);
        const mfrId = isVendor ? user?.vendorId : values.manufacturerId;
        if (mfrId) fd.append("manufacturerId", mfrId);
        if (values.brandId) fd.append("brandId", values.brandId);

        const response = await createAiProduct(fd);
        markAttemptedAi();
        const productId = response?.data?.data?.product?.id;
        if (response?.data?.success && productId) {
          toast.success("AI product created successfully");
          refreshBillingBalance();
          navigate(`/edit-inventory/${productId}`);
        } else {
          toast.error(response?.data?.message || "Failed to create AI product");
        }
      } catch (err) {
        markAttemptedAi();
        const code = err?.response?.data?.code;
        if (
          err?.response?.status === 402 &&
          (code === "INSUFFICIENT_CREDITS" || code === "TRIAL_AVAILABLE")
        ) {
          setPaywall({
            balance: err.response.data.balance,
            message: err.response.data.message,
            trialAvailable: code === "TRIAL_AVAILABLE",
          });
          refreshBillingBalance();
        } else if (err?.code === "ECONNABORTED") {
          toast.error(
            "The AI took too long to respond. Please try again with clearer photos."
          );
        } else if (
          err?.response?.status === 400 &&
          code === "INVALID_PRODUCT_IMAGE"
        ) {
          setInvalidImage({
            rejectionReason: err.response.data.rejectionReason,
            message: err.response.data.message,
          });
        } else if (err?.response?.status === 400) {
          toast.error(
            err.response?.data?.message ||
              "Please check your inputs and try again"
          );
        } else {
          toast.error(
            err?.response?.data?.message ||
              "Something went wrong generating this listing. Please try again with clearer photos."
          );
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    getCategoryListingPaginate({ page: 1, limit: 10, search: "" })((res) => {
      if (res?.statusCode === 200) setCategoryOptions(res?.results || []);
    });
    if (!isVendor) {
      getManufacturesListingPaginate({ page: 1, limit: 10, search: "" })(
        (res) => {
          if (res?.statusCode === 200)
            setManufacturerOptions(res?.results || []);
        }
      );
    }
  }, [isVendor]);

  useEffect(() => {
    if (!formik.values.categoryId) {
      setSubCategoryOptions([]);
      return;
    }
    getSubCategoryListingPaginate({
      page: 1,
      limit: 100,
      search: "",
      categoryIds: [formik.values.categoryId],
    })((res) => {
      if (res?.statusCode === 200) setSubCategoryOptions(res?.results || []);
      else setSubCategoryOptions([]);
    });
  }, [formik.values.categoryId]);

  useEffect(() => {
    const vendorId = isVendor
      ? user?.vendorId
      : formik.values.manufacturerId || null;
    if (!vendorId) {
      setBrandOptions([]);
      return;
    }
    getBrandListing({ vendorId, page: 1, pageSize: 10, search: "" })((res) => {
      if (res?.statusCode === 200) {
        const opts = (res?.data || []).map((b) => ({
          value: b.id,
          label: b.name,
        }));
        setBrandOptions(opts);
        if (opts.length === 1) {
          formik.setFieldValue("brandId", opts[0].value);
          formik.setFieldValue("selectedBrand", opts[0]);
        }
      } else setBrandOptions([]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.manufacturerId, isVendor, user?.vendorId]);

  let typingTimer;
  const debounce = (fn, ms = 300) => {
    clearTimeout(typingTimer);
    typingTimer = setTimeout(fn, ms);
  };

  const loadCategoryOptions = (input) =>
    new Promise((resolve) => {
      debounce(() => {
        getCategoryListingPaginate({ page: 1, limit: 10, search: input })(
          (res) => {
            if (res?.statusCode === 200) {
              setCategoryOptions(res?.results || []);
              resolve(res?.results || []);
            } else resolve([]);
          }
        );
      });
    });

  const loadManufacturerOptions = (input) =>
    new Promise((resolve) => {
      debounce(() => {
        getManufacturesListingPaginate({ page: 1, limit: 10, search: input })(
          (res) => {
            if (res?.statusCode === 200) {
              setManufacturerOptions(res?.results || []);
              resolve(res?.results || []);
            } else resolve([]);
          }
        );
      });
    });

  const loadBrandOptions = (input) =>
    new Promise((resolve) => {
      const vendorId = isVendor
        ? user?.vendorId
        : formik.values.manufacturerId;
      if (!vendorId) return resolve([]);
      debounce(() => {
        getBrandListing({
          vendorId,
          page: 1,
          pageSize: 10,
          search: input,
        })((res) => {
          if (res?.statusCode === 200) {
            const opts = (res?.data || []).map((b) => ({
              value: b.id,
              label: b.name,
            }));
            setBrandOptions(opts);
            resolve(opts);
          } else resolve([]);
        });
      });
    });

  const validateImage = (file) => {
    if (!ACCEPTED_MIME.includes(file.type))
      return "Only JPG, JPEG, or PNG allowed";
    if (file.size > MAX_IMAGE_BYTES) return "Max file size is 10 MB";
    return "";
  };

  const setImage = (side, file) => {
    const err = validateImage(file);
    if (err) {
      setImageErrors((p) => ({ ...p, [side]: err }));
      return;
    }
    setImageErrors((p) => ({ ...p, [side]: "" }));
    const preview = URL.createObjectURL(file);
    if (side === "front") {
      if (frontPreview) URL.revokeObjectURL(frontPreview);
      setFrontFile(file);
      setFrontPreview(preview);
    } else {
      if (backPreview) URL.revokeObjectURL(backPreview);
      setBackFile(file);
      setBackPreview(preview);
    }
  };

  const removeImage = (side) => {
    if (side === "front") {
      if (frontPreview) URL.revokeObjectURL(frontPreview);
      setFrontFile(null);
      setFrontPreview("");
    } else {
      if (backPreview) URL.revokeObjectURL(backPreview);
      setBackFile(null);
      setBackPreview("");
    }
  };

  const isReady =
    frontFile &&
    formik.values.categoryId &&
    formik.values.subCategoryId &&
    (isVendor || formik.values.manufacturerId);

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      borderRadius: "4px",
      padding: "0",
      fontSize: "11px",
      minHeight: "32px",
      borderColor: state.isFocused ? "#008695" : "#e2e8f0",
      boxShadow: state.isFocused
        ? "0 0 0 3px rgba(0, 134, 149, 0.15)"
        : "none",
      "&:hover": { borderColor: "#008695" },
    }),
    valueContainer: (base) => ({ ...base, padding: "0 8px" }),
    input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: "11px" }),
    indicatorsContainer: (base) => ({ ...base, height: "32px" }),
    dropdownIndicator: (base) => ({ ...base, padding: "4px" }),
    clearIndicator: (base) => ({ ...base, padding: "4px" }),
    menu: (base) => ({ ...base, fontSize: "11px", zIndex: 20 }),
    option: (base) => ({ ...base, fontSize: "11px", padding: "6px 10px" }),
    placeholder: (base) => ({ ...base, fontSize: "11px", color: "#94a3b8" }),
    singleValue: (base) => ({ ...base, fontSize: "11px" }),
  };

  return (
    <div className="ai-inventory-page">
      <style>{`
        .ai-inventory-page h3 { font-size: 18px !important; font-weight: 600 !important; }
        .ai-inventory-page h4 { font-size: 15px !important; font-weight: 600 !important; }
        .ai-inventory-page h5 { font-size: 14px !important; font-weight: 600 !important; }
        .ai-inventory-page h6 { font-size: 12px !important; font-weight: 600 !important; }
        .ai-inventory-page p { font-size: 12px; margin: 4px 0 0; font-weight: 400; }
        .ai-inventory-page small, .ai-inventory-page .small { font-size: 11px !important; font-weight: 400 !important; }
        .ai-inventory-page .form-label { font-size: 11px !important; margin-bottom: 4px !important; font-weight: 500 !important; }
        .ai-inventory-page .fw-semibold { font-weight: 500 !important; }
        .ai-inventory-page .fw-bold, .ai-inventory-page .fw-bolder { font-weight: 600 !important; }
        .ai-inventory-page .card-header span { font-size: 13px !important; font-weight: 600 !important; }
        .ai-inventory-page .btn { font-size: 12px; font-weight: 500; }
        .ai-inventory-page .ai-error { font-size: 11px !important; color: #dc2626; margin-top: 4px; font-weight: 500; }

        /* Hide the global topbar on this page — the page header is the header */
        body:has(.ai-inventory-page) #page-topbar { display: none !important; }

        /* Page header pinned at the top, full-width */
        .ai-inventory-page .page-header-sticky {
          position: fixed !important;
          top: 0 !important;
          left: var(--vz-vertical-menu-width, 208px) !important;
          right: 0 !important;
          width: auto !important;
          padding: 12px clamp(20px, 3vw, 48px) !important;
          margin: 0 !important;
          background: #f9fafb !important;
          border-bottom: 1px solid #e5e7eb !important;
          z-index: 1050 !important;
        }
        .ai-inventory-page { padding-top: 96px !important; }
        @media (max-width: 991px) {
          .ai-inventory-page .page-header-sticky { padding: 10px 20px !important; }
        }
        @media (max-width: 767.98px) {
          body:has(.ai-inventory-page) .mobile-topbar { display: none !important; }
          body:has(.ai-inventory-page) .main-content { padding-top: 0 !important; margin-left: 0 !important; }
          /* Trim horizontal gutter across the whole page-content on mobile */
          body:has(.ai-inventory-page) .page-content {
            padding-left: 8px !important;
            padding-right: 8px !important;
          }
          body:has(.ai-inventory-page) .container,
          body:has(.ai-inventory-page) .container-fluid {
            padding-left: 0 !important;
            padding-right: 0 !important;
          }
          .ai-inventory-page .row { margin-left: -4px !important; margin-right: -4px !important; }
          .ai-inventory-page .row > [class*="col-"] { padding-left: 4px !important; padding-right: 4px !important; }
          .ai-inventory-page .page-header-sticky {
            left: 0 !important;
            right: 0 !important;
            padding: 10px 12px !important;
          }
          .ai-inventory-page { padding-top: 60px !important; }
        }
        @media (max-width: 575px) {
          .ai-inventory-page .page-header-sticky { padding: 10px 10px !important; }
        }
        body:has(.ai-inventory-page) .page-content {
          padding-top: 0 !important;
          padding-bottom: 24px !important;
        }
        body:has(.ai-inventory-page) #layout-wrapper.no-topbar .page-content {
          padding-top: 0 !important;
          padding-bottom: 24px !important;
        }
        body:has(.ai-inventory-page) .main-content { padding-top: 0 !important; }
        .ai-inventory-page > .page-header-sticky { margin-top: 0 !important; }

        /* Back button */
        .ai-inventory-page .ai-back-btn {
          display: inline-flex; align-items: center; justify-content: center;
          width: 30px; height: 30px; border-radius: 4px;
          font-size: 16px; color: #475569; background: #fff;
          border: 1px solid #e2e8f0; cursor: pointer;
          margin-right: 8px; flex-shrink: 0;
          transition: all 0.15s ease;
        }
        .ai-inventory-page .ai-back-btn:hover { color: #008695; border-color: #008695; }
        .ai-inventory-page .ai-back-btn[disabled] { opacity: 0.5; cursor: not-allowed; }

        /* Card containers */
        .ai-inventory-page .card {
          border: 1px solid #e2e8f0 !important;
          border-radius: 10px !important;
          box-shadow: none !important;
          margin-bottom: 12px !important;
        }
        .ai-inventory-page .card-header {
          background: #f8fafc !important;
          padding: 10px 14px !important;
          border-bottom: 1px solid #e2e8f0 !important;
        }
        .ai-inventory-page .card-body { padding: 14px !important; }
        .ai-inventory-page .form-group,
        .ai-inventory-page .mb-4 { margin-bottom: 12px !important; }

        /* Header title + subtitle */
        .ai-inventory-page .page-header-sticky h3 {
          font-size: 18px !important;
          margin: 0 !important;
          line-height: 1.25;
          display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
        }
        .ai-inventory-page .page-header-sticky p {
          font-size: 11px !important;
          color: #64748b !important;
          margin: 2px 0 0 !important;
        }

        /* AI pill in title */
        .ai-inventory-page .ai-pill {
          display: inline-flex; align-items: center; gap: 4px;
          font-size: 10px !important; font-weight: 600 !important;
          padding: 3px 8px !important;
          background: linear-gradient(135deg, rgba(99,102,241,0.1), rgba(168,85,247,0.1));
          color: #6d28d9 !important;
          border: 1px solid rgba(168,85,247,0.2);
          border-radius: 999px;
          white-space: nowrap;
        }
        .ai-inventory-page .ai-pill svg { width: 10px; height: 10px; }

        /* CTA + note */
        .ai-inventory-page .ai-header-cta {
          display: flex; flex-direction: column; align-items: flex-end;
          gap: 4px; flex-shrink: 0;
        }
        .ai-inventory-page .ai-generate-btn {
          display: inline-flex; align-items: center; justify-content: center;
          gap: 6px;
          height: 34px !important;
          padding: 0 16px !important;
          font-size: 12px !important;
          font-weight: 600 !important;
          border-radius: 4px !important;
          background: linear-gradient(135deg, #6366f1 0%, #a855f7 50%, #ec4899 100%) !important;
          border: none !important;
          color: #fff !important;
          box-shadow: 0 2px 8px rgba(168, 85, 247, 0.25);
          white-space: nowrap;
        }
        .ai-inventory-page .ai-generate-btn:hover:not(:disabled) {
          filter: brightness(1.05);
          transform: translateY(-1px);
        }
        .ai-inventory-page .ai-generate-btn:disabled {
          background: #cbd5e1 !important; box-shadow: none;
        }
        .ai-inventory-page .ai-generate-btn-trial {
          background: linear-gradient(135deg, #008695 0%, #10b981 100%) !important;
        }
        .ai-inventory-page .ai-generate-btn-empty {
          background: #dc2626 !important;
        }
        .ai-inventory-page .ai-icon { width: 12px; height: 12px; }
        .ai-inventory-page .ai-cost-note {
          font-size: 10px; color: #64748b; font-weight: 500;
        }
        .ai-inventory-page .ai-cost-note.is-trial { color: #059669; }
        .ai-inventory-page .ai-cost-note.is-empty { color: #dc2626; }
        .ai-inventory-page .ai-trial-hint {
          display: inline-flex; align-items: center; gap: 4px;
          font-size: 10px; color: #059669; background: transparent;
          border: none; padding: 0; cursor: pointer; font-weight: 600;
        }
        .ai-inventory-page .ai-trial-hint:hover { text-decoration: underline; }

        /* Dropzone */
        .ai-inventory-page .ai-dropzone-wrap { margin-bottom: 10px; }
        .ai-inventory-page .ai-dropzone-label {
          display: inline-flex; align-items: center; gap: 5px;
          font-size: 11px; font-weight: 600; color: #475569;
          margin-bottom: 6px;
        }
        .ai-inventory-page .ai-dropzone-label svg { width: 12px; height: 12px; }
        .ai-inventory-page .ai-dropzone {
          position: relative;
          border: 1.5px dashed #cbd5e1;
          border-radius: 8px;
          aspect-ratio: 4 / 3;
          background: #f8fafc;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; overflow: hidden;
          transition: all 0.15s ease;
        }
        .ai-inventory-page .ai-dropzone:hover { border-color: #008695; background: rgba(0,134,149,0.03); }
        .ai-inventory-page .ai-dropzone.dragging { border-color: #008695; background: rgba(0,134,149,0.05); }
        .ai-inventory-page .ai-dropzone.has-image { border-style: solid; border-color: #e2e8f0; background: #fff; padding: 0; cursor: default; }
        .ai-inventory-page .ai-dropzone.has-error { border-color: #dc2626; background: rgba(220,38,38,0.03); }
        .ai-inventory-page .ai-dropzone-empty {
          display: flex; flex-direction: column; align-items: center; gap: 4px;
          padding: 12px;
        }
        .ai-inventory-page .ai-upload-icon { width: 24px; height: 24px; color: #94a3b8; }
        .ai-inventory-page .ai-dropzone-primary { font-size: 12px; font-weight: 500; color: #475569; }
        .ai-inventory-page .ai-dropzone-primary .link { color: #008695; font-weight: 600; }
        .ai-inventory-page .ai-dropzone-secondary { font-size: 10px; color: #94a3b8; }
        .ai-inventory-page .ai-dropzone-preview {
          width: 100%; height: 100%; object-fit: cover;
        }
        .ai-inventory-page .ai-dropzone-remove {
          position: absolute; top: 6px; right: 6px;
          width: 22px; height: 22px; border-radius: 50%;
          background: rgba(15,23,42,0.65); color: #fff;
          border: none; display: inline-flex; align-items: center; justify-content: center;
          cursor: pointer; padding: 0;
        }
        .ai-inventory-page .ai-dropzone-remove:hover { background: rgba(15,23,42,0.85); }
        .ai-inventory-page .ai-dropzone-remove svg { width: 12px; height: 12px; }

        /* Guidelines block */
        .ai-inventory-page .ai-guidelines {
          margin-top: 14px;
          padding: 12px;
          background: #f8fafc;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
        }
        .ai-inventory-page .ai-guidelines-heading {
          display: inline-flex; align-items: center; gap: 6px;
          font-size: 12px; font-weight: 600; color: #0f172a;
          margin-bottom: 10px;
        }
        .ai-inventory-page .ai-guidelines-heading svg { width: 13px; height: 13px; color: #008695; }
        .ai-inventory-page .ai-guideline-row { display: flex; gap: 10px; align-items: flex-start; }
        .ai-inventory-page .ai-guideline-row + .ai-guideline-row { margin-top: 10px; }
        .ai-inventory-page .ai-guideline-row-title {
          display: inline-flex; align-items: center; gap: 4px;
          font-size: 11px; font-weight: 700;
          min-width: 54px; flex-shrink: 0;
        }
        .ai-inventory-page .ai-guideline-row-do .ai-guideline-row-title { color: #059669; }
        .ai-inventory-page .ai-guideline-row-dont .ai-guideline-row-title { color: #dc2626; }
        .ai-inventory-page .ai-guideline-row-items {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 8px;
          flex: 1;
        }
        .ai-inventory-page .ai-guideline-item {
          display: flex; flex-direction: column; gap: 4px;
          font-size: 10px; color: #475569;
        }
        .ai-inventory-page .ai-guideline-item img {
          width: 100%; aspect-ratio: 1 / 1; object-fit: cover;
          border-radius: 6px; border: 1px solid #e2e8f0;
        }
        .ai-inventory-page .ai-guideline-item span { text-align: center; }

        /* Loading overlay */
        .ai-inventory-page .ai-loading-overlay {
          position: fixed; inset: 0;
          background: rgba(15,23,42,0.7);
          display: flex; align-items: center; justify-content: center;
          z-index: 9999;
          padding: 20px;
        }
        .ai-inventory-page .ai-loading-card {
          background: #fff; border-radius: 14px;
          padding: 28px 24px;
          max-width: 340px; width: 100%;
          text-align: center;
          box-shadow: 0 20px 60px rgba(0,0,0,0.3);
        }
        .ai-inventory-page .ai-loading-orb {
          width: 56px; height: 56px;
          border-radius: 50%;
          background: linear-gradient(135deg, #6366f1 0%, #a855f7 50%, #ec4899 100%);
          color: #fff; font-size: 22px;
          display: inline-flex; align-items: center; justify-content: center;
          margin-bottom: 14px;
          box-shadow: 0 6px 20px rgba(168,85,247,0.35);
          animation: aiPulse 1.6s ease-in-out infinite;
        }
        @keyframes aiPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        .ai-inventory-page .ai-loading-status {
          font-size: 13px; font-weight: 600; color: #0f172a;
          margin-bottom: 12px;
        }
        .ai-inventory-page .ai-loading-bar {
          height: 4px; background: #e2e8f0; border-radius: 999px;
          overflow: hidden; margin-bottom: 12px;
        }
        .ai-inventory-page .ai-loading-bar span {
          display: block; height: 100%;
          background: linear-gradient(90deg, #6366f1, #ec4899);
          animation: aiBar 2.4s ease-in-out infinite;
          border-radius: 999px;
        }
        @keyframes aiBar {
          0% { width: 8%; margin-left: 0; }
          50% { width: 70%; }
          100% { width: 8%; margin-left: 92%; }
        }
        .ai-inventory-page .ai-loading-note { font-size: 11px; color: #64748b; }

        /* Responsive */
        @media (max-width: 991.98px) {
          .ai-inventory-page .card-body { padding: 12px !important; }
        }
        @media (max-width: 767.98px) {
          .ai-inventory-page .page-header-sticky {
            flex-wrap: wrap !important;
            gap: 8px !important;
          }
          .ai-inventory-page .page-header-sticky h3 { font-size: 15px !important; font-weight: 600 !important; }
          .ai-inventory-page .page-header-sticky p { font-size: 10px !important; margin-top: 2px !important; font-weight: 400 !important; }
          .ai-inventory-page .ai-pill { font-size: 9px !important; padding: 2px 6px !important; font-weight: 500 !important; }
          .ai-inventory-page .card { margin-bottom: 10px !important; border-radius: 8px !important; }
          .ai-inventory-page .card-header { padding: 8px 10px !important; }
          .ai-inventory-page .card-header span { font-size: 12px !important; }
          .ai-inventory-page .card-body { padding: 10px !important; }
          .ai-inventory-page .form-group,
          .ai-inventory-page .mb-4 { margin-bottom: 10px !important; }
          .ai-inventory-page .ai-guidelines { padding: 10px !important; margin-top: 12px !important; }

          /* Compact image dropzones on mobile */
          .ai-inventory-page .ai-dropzone-wrap { margin-bottom: 6px !important; }
          .ai-inventory-page .ai-dropzone-label {
            font-size: 10px !important;
            font-weight: 500 !important;
            margin-bottom: 4px !important;
          }
          .ai-inventory-page .ai-dropzone-label svg { width: 10px !important; height: 10px !important; }
          .ai-inventory-page .ai-dropzone {
            aspect-ratio: unset !important;
            height: 110px !important;
            border-radius: 6px !important;
            border-width: 1px !important;
          }
          .ai-inventory-page .ai-dropzone-empty {
            padding: 6px !important;
            gap: 2px !important;
          }
          .ai-inventory-page .ai-upload-icon { width: 18px !important; height: 18px !important; }
          .ai-inventory-page .ai-dropzone-primary { font-size: 10px !important; font-weight: 500 !important; }
          .ai-inventory-page .ai-dropzone-secondary { font-size: 8px !important; font-weight: 400 !important; }
          .ai-inventory-page .ai-dropzone-remove {
            top: 4px !important; right: 4px !important;
            width: 20px !important; height: 20px !important;
          }
          .ai-inventory-page .ai-dropzone-remove svg { width: 10px !important; height: 10px !important; }
          /* Move CTA to a fixed bottom action bar on mobile */
          .ai-inventory-page .ai-header-cta {
            position: fixed !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            width: 100% !important;
            z-index: 900;
            background: #fff;
            border-top: 1px solid #e2e8f0;
            padding: 10px 14px calc(10px + env(safe-area-inset-bottom, 0px)) !important;
            display: flex;
            flex-direction: column;
            align-items: stretch;
            gap: 4px;
            box-shadow: 0 -2px 10px rgba(15,23,42,0.06);
          }
          .ai-inventory-page .ai-generate-btn {
            width: 100%;
            height: 42px !important;
            font-size: 13px !important;
          }
          .ai-inventory-page .ai-cost-note { text-align: center; }
          .ai-inventory-page .ai-trial-hint { justify-content: center; }
          /* Reserve room for the fixed bar so form fields don't sit behind it */
          .ai-inventory-page {
            padding-bottom: calc(90px + env(safe-area-inset-bottom, 0px));
          }

          /* Stack columns */
          .ai-inventory-page .row > .col-md-7,
          .ai-inventory-page .row > .col-md-5,
          .ai-inventory-page .row > [class*="col-md-"] {
            flex: 0 0 100%;
            max-width: 100%;
          }
          /* Front + Back dropzones still side by side on mobile */
          .ai-inventory-page .card-body .row > .col-md-6 {
            flex: 0 0 50%;
            max-width: 50%;
            padding-left: 6px !important;
            padding-right: 6px !important;
          }

          /* Guidelines: stack title above items, items 4 across, tighter typography */
          .ai-inventory-page .ai-guidelines-heading {
            font-size: 11px !important;
            font-weight: 500 !important;
            margin-bottom: 8px !important;
            gap: 4px !important;
          }
          .ai-inventory-page .ai-guidelines-heading svg { width: 11px !important; height: 11px !important; }
          .ai-inventory-page .ai-guideline-row {
            flex-direction: column;
            gap: 4px !important;
          }
          .ai-inventory-page .ai-guideline-row + .ai-guideline-row { margin-top: 8px !important; }
          .ai-inventory-page .ai-guideline-row-title {
            font-size: 10px !important;
            font-weight: 600 !important;
            gap: 3px !important;
            min-width: 0 !important;
          }
          .ai-inventory-page .ai-guideline-row-title svg { width: 10px !important; height: 10px !important; }
          .ai-inventory-page .ai-guideline-row-items {
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 5px !important;
          }
          .ai-inventory-page .ai-guideline-item {
            font-size: 7px !important;
            font-weight: 400 !important;
            line-height: 1.2 !important;
            gap: 3px !important;
          }
        }
        @media (max-width: 480px) {
          .ai-inventory-page .card-body .row > .col-md-6 { padding: 0 4px !important; }
          .ai-inventory-page .ai-guideline-item { font-size: 7px !important; }
          .ai-inventory-page .ai-guideline-row-title { font-size: 9px !important; }
          .ai-inventory-page .ai-guidelines-heading { font-size: 10px !important; }
          .ai-inventory-page .ai-loading-card { padding: 20px 16px; }
        }
      `}</style>

      <div className="page-header-sticky d-flex align-items-center justify-content-between flex-wrap gap-3">
            <div className="d-flex align-items-start">
              <button
                type="button"
                className="ai-back-btn back-btn"
                onClick={() => !submitting && navigate(-1)}
                disabled={submitting}
                aria-label="Back"
              >
                <IoMdArrowBack />
              </button>
              <div>
                <h3 className="mb-0 fw-bold">
                  AI Product Listing
                  <span className="ai-pill">
                    <FaWandMagicSparkles /> Powered by Getto AI
                  </span>
                </h3>
                <p className="text-muted">
                  Upload photos and let AI draft your listing.
                </p>
              </div>
            </div>

            <div className="ai-header-cta">
              {isTrialOnly ? (
                <>
                  <Button
                    type="button"
                    className="ai-generate-btn ai-generate-btn-trial"
                    onClick={() => navigate("/billing")}
                  >
                    <FaGift className="ai-icon" />
                    Claim {trialGrantAmount} free credits
                  </Button>
                  <div className="ai-cost-note is-trial">
                    On the house · no card required
                  </div>
                </>
              ) : isOutOfCredits ? (
                <>
                  <Button
                    type="button"
                    className="ai-generate-btn ai-generate-btn-empty"
                    onClick={() => navigate("/billing")}
                  >
                    <FaWandMagicSparkles className="ai-icon" />
                    Get credits
                  </Button>
                  <div className="ai-cost-note is-empty">
                    0 credits · Buy a plan or top-up
                  </div>
                </>
              ) : (
                <>
                  <Button
                    type="button"
                    className="ai-generate-btn"
                    disabled={!isReady || submitting}
                    onClick={() => formik.handleSubmit()}
                  >
                    <FaWandMagicSparkles className="ai-icon" />
                    Generate Product
                  </Button>
                  <div className="ai-cost-note">Uses 1 credit</div>
                  {hasClaimableTrial && (
                    <button
                      type="button"
                      className="ai-trial-hint"
                      onClick={() => navigate("/billing")}
                    >
                      <FaGift />
                      {trialGrantAmount} free credits waiting · Claim
                    </button>
                  )}
                </>
              )}
            </div>
      </div>

      <Form onSubmit={formik.handleSubmit} noValidate>
        <Row>
          <Col md={7}>
            <Card className="mb-4 shadow-sm">
              <Card.Header className="bg-light">
                <span style={{ fontSize: "15px", fontWeight: "600" }}>
                  Product Photos
                </span>
              </Card.Header>
              <Card.Body className="p-4">
                <Row>
                  <Col md={6}>
                    <Dropzone
                      label="Front View"
                      preview={frontPreview}
                      error={imageErrors.front}
                      onFile={(f) => setImage("front", f)}
                      onRemove={() => removeImage("front")}
                    />
                  </Col>
                  <Col md={6}>
                    <Dropzone
                      label="Back View (optional)"
                      preview={backPreview}
                      error={imageErrors.back}
                      onFile={(f) => setImage("back", f)}
                      onRemove={() => removeImage("back")}
                    />
                  </Col>
                </Row>

                <div className="ai-guidelines">
                  <div className="ai-guidelines-heading">
                    <FiImage /> Photo guidelines for the best AI results
                  </div>

                  <div className="ai-guideline-row ai-guideline-row-do">
                    <div className="ai-guideline-row-title">
                      <FiCheck /> Do
                    </div>
                    <div className="ai-guideline-row-items">
                      <GuidelineItem variant="good-lit" label="Well-lit" />
                      <GuidelineItem variant="good-bg" label="Neutral background" />
                      <GuidelineItem variant="good-frame" label="Fills the frame" />
                      <GuidelineItem variant="good-angle" label="Straight-on angle" />
                    </div>
                  </div>

                  <div className="ai-guideline-row ai-guideline-row-dont">
                    <div className="ai-guideline-row-title">
                      <FaXmark /> Avoid
                    </div>
                    <div className="ai-guideline-row-items">
                      <GuidelineItem variant="bad-lit" label="Dim or dark" />
                      <GuidelineItem variant="bad-bg" label="Busy background" />
                      <GuidelineItem variant="bad-frame" label="Product too small" />
                      <GuidelineItem variant="bad-blur" label="Blurry or filtered" />
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={5}>
            <Card className="mb-4 shadow-sm">
              <Card.Header className="bg-light">
                <span style={{ fontSize: "15px", fontWeight: "600" }}>
                  Category
                </span>
              </Card.Header>
              <Card.Body className="p-4">
                <Form.Group className="mb-4">
                  <Form.Label
                    className="fw-semibold"
                    style={{ fontSize: "11px" }}
                  >
                    Category *
                  </Form.Label>
                  <AsyncSelect
                    cacheOptions
                    defaultOptions={categoryOptions}
                    loadOptions={loadCategoryOptions}
                    value={formik.values.selectedCategory}
                    onChange={(opt) => {
                      formik.setValues({
                        ...formik.values,
                        categoryId: opt?.value || "",
                        selectedCategory: opt,
                        subCategoryId: "",
                        selectedSubCategory: null,
                      });
                      setSubCategoryOptions([]);
                    }}
                    onBlur={() => formik.setFieldTouched("categoryId", true)}
                    placeholder="Choose category"
                    isClearable
                    styles={selectStyles}
                  />
                  {formik.touched.categoryId && formik.errors.categoryId && (
                    <div className="ai-error">{formik.errors.categoryId}</div>
                  )}
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label
                    className="fw-semibold"
                    style={{ fontSize: "11px" }}
                  >
                    Sub-Category *
                  </Form.Label>
                  <Select
                    key={formik.values.categoryId || "no-cat"}
                    options={subCategoryOptions}
                    value={formik.values.selectedSubCategory}
                    onChange={(opt) => {
                      formik.setValues({
                        ...formik.values,
                        subCategoryId: opt?.value || "",
                        selectedSubCategory: opt,
                      });
                    }}
                    onBlur={() => formik.setFieldTouched("subCategoryId", true)}
                    isDisabled={!formik.values.categoryId}
                    placeholder={
                      formik.values.categoryId
                        ? "Choose sub-category"
                        : "Select category first"
                    }
                    isClearable
                    styles={selectStyles}
                  />
                  {formik.touched.subCategoryId &&
                    formik.errors.subCategoryId && (
                      <div className="ai-error">
                        {formik.errors.subCategoryId}
                      </div>
                    )}
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label
                    className="fw-semibold"
                    style={{ fontSize: "11px" }}
                  >
                    Vendor {!isVendor && "*"}
                  </Form.Label>
                  {isVendor ? (
                    <AsyncSelect
                      value={vendorSelfOption}
                      isDisabled
                      styles={selectStyles}
                    />
                  ) : (
                    <>
                      <AsyncSelect
                        cacheOptions
                        defaultOptions={manufacturerOptions}
                        loadOptions={loadManufacturerOptions}
                        value={formik.values.selectedManufacturer}
                        onChange={(opt) => {
                          formik.setValues({
                            ...formik.values,
                            manufacturerId: opt?.value || "",
                            selectedManufacturer: opt,
                            brandId: "",
                            selectedBrand: null,
                          });
                        }}
                        onBlur={() =>
                          formik.setFieldTouched("manufacturerId", true)
                        }
                        placeholder="Choose vendor"
                        isClearable
                        styles={selectStyles}
                      />
                      {formik.touched.manufacturerId &&
                        formik.errors.manufacturerId && (
                          <div className="ai-error">
                            {formik.errors.manufacturerId}
                          </div>
                        )}
                    </>
                  )}
                </Form.Group>

                <Form.Group className="mb-2">
                  <Form.Label
                    className="fw-semibold"
                    style={{ fontSize: "11px" }}
                  >
                    Brand
                  </Form.Label>
                  <AsyncSelect
                    key={`brand-${
                      isVendor ? user?.vendorId : formik.values.manufacturerId
                    }`}
                    cacheOptions
                    defaultOptions={brandOptions}
                    loadOptions={loadBrandOptions}
                    value={formik.values.selectedBrand}
                    onChange={(opt) => {
                      formik.setValues({
                        ...formik.values,
                        brandId: opt?.value || "",
                        selectedBrand: opt,
                      });
                    }}
                    isDisabled={!isVendor && !formik.values.manufacturerId}
                    placeholder={
                      !isVendor && !formik.values.manufacturerId
                        ? "Select vendor first"
                        : "Choose brand"
                    }
                    isClearable
                    styles={selectStyles}
                  />
                </Form.Group>

                <div
                  className="text-muted mt-3"
                  style={{ fontSize: "10px", lineHeight: 1.5 }}
                >
                  You'll add pricing, stock, and other details on the next step
                  once AI drafts the listing.
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

      </Form>

      {submitting && (
        <div className="ai-loading-overlay" role="dialog" aria-live="polite">
          <div className="ai-loading-card">
            <div className="ai-loading-orb">
              <FaWandMagicSparkles />
            </div>
            <div className="ai-loading-status">
              {LOADING_STEPS[loadingStep].text}
            </div>
            <div className="ai-loading-bar">
              <span />
            </div>
            <div className="ai-loading-note">
              This can take up to a minute. Please keep this tab open.
            </div>
          </div>
        </div>
      )}

      <PaywallModal
        isOpen={!!paywall}
        toggle={() => setPaywall(null)}
        balance={paywall?.balance}
        message={paywall?.message}
        trialAvailable={paywall?.trialAvailable}
      />

      <InvalidImageModal
        isOpen={!!invalidImage}
        toggle={() => setInvalidImage(null)}
        rejectionReason={invalidImage?.rejectionReason}
        message={invalidImage?.message}
      />
    </div>
  );
};

export default CreateAiInventory;
